import { Matrix, Quaternion, Vector3 } from "@babylonjs/core";
import { sampleRepairSwing, type RepairSwingSample } from "../character/repair-swing.ts";
import { SWORD_GRIP_ROTATION } from "./character-rig.ts";

/** The hammer mounts in the right-hand grip like the cutlass: handle +y, striking face −x (tools/models/hammer.py). */
export const HAMMER_GRIP_ROTATION = SWORD_GRIP_ROTATION;
/** Face centre in hammer space (hammer.py HEAD_Z, FACE_X). */
const HAMMER_HEAD = 0.215, HAMMER_FACE = 0.075;
/** Nail head on the plank's holder face, in plank space (plank.py NAIL_X, NAIL_Y, face + head). */
const PLANK_NAIL = { x: 0.13, y: 0.015, z: -0.02 } as const;
/** Plank face toward the holder (plank.py HALF_THICKNESS). */
const PLANK_FACE = -0.014;
/** Windup: the head tips back over the wrist while the hand lifts and draws back from its impact grip. */
const HAMMER_WINDUP_ANGLE = 0.9;
const HAMMER_WINDUP_LIFT = 0.14, HAMMER_WINDUP_DRAW = 0.04;
/** The head comes in slightly from the right, across the body's centre line. */
const HAMMER_ROLL = 0.22;
/** Plank and hammer rise into place from this far below the work. */
const SETUP_DROP = 0.16;
/** Flat left palm on the plank's left end; the grip anchor sits this far into the timber. */
const BRACE = { x: -0.14, y: -0.01, palmDepth: 0.02 } as const;

/**
 * Action-frame hand rotation from where the palm faces and the fingers point: hanging hands
 * face their palm along local −z with the index along local −y (as Avatar's facing poses).
 */
function facingToRef(palm: Vector3, fingers: Vector3, out: Quaternion): Quaternion {
  const z = palm.normalizeToNew().scaleInPlace(-1);
  const y = fingers.subtract(z.scale(Vector3.Dot(fingers, z))).normalize().scaleInPlace(-1);
  return Quaternion.FromRotationMatrixToRef(Matrix.FromXYZAxesToRef(Vector3.Cross(y, z), y, z, new Matrix()), out);
}

/** The bracing left palm presses into the plank, fingers up and slightly inward. */
export const REPAIR_BRACE_PALM = new Vector3(0, 0, 1);
export const REPAIR_BRACE_FINGERS = new Vector3(0.3, 1, 0);

/**
 * Hand rotation = hammer rotation · (grip node rotation · hammer mount)⁻¹; the right hand's
 * grip node turns +π/2 about x (actors.ts). These are the hand's palm normal (local −z)
 * and finger direction (local −y) expressed in hammer space.
 */
const HAMMER_TO_HAND = Quaternion.RotationYawPitchRoll(0, Math.PI / 2, 0).multiply(HAMMER_GRIP_ROTATION).conjugate();
const HAMMER_PALM = new Vector3(0, 0, -1).applyRotationQuaternion(HAMMER_TO_HAND);
const HAMMER_FINGERS = new Vector3(0, -1, 0).applyRotationQuaternion(HAMMER_TO_HAND);

/**
 * Hammer-and-plank repair in a rig's action frame (+x right, +y up, +z forward):
 * the plank is held over the hole facing the worker, the left palm braces its
 * left end and the right hand drives the hammer face onto the nail. Allocation-free.
 */
export class RepairPose {
  /** Plank centre; the plank lies in the frame's x/y plane, face toward −z. */
  readonly plank = new Vector3();
  /** Right-hand grip anchor (the hammer's origin), palm normal and finger direction. */
  readonly grip = new Vector3();
  readonly palm = new Vector3();
  readonly fingers = new Vector3();
  /** Frame-local hand rotations, for rigs posed by rotation rather than palm and finger directions. */
  readonly gripRotation = Quaternion.Identity();
  readonly braceRotation = facingToRef(REPAIR_BRACE_PALM, REPAIR_BRACE_FINGERS, Quaternion.Identity());
  /** Left-hand grip anchor; its palm is REPAIR_BRACE_PALM, its fingers REPAIR_BRACE_FINGERS. */
  readonly brace = new Vector3();
  readonly swing: RepairSwingSample = { raise: 0, setup: 0 };
  private readonly basis = new Matrix();
  /** Hammer basis in the action frame: x (−striking direction), y (handle), z = x × y. */
  private readonly axisX = new Vector3();
  private readonly axisY = new Vector3();
  private readonly axisZ = new Vector3();

  /** `rest` is the plank centre once it is in place. */
  sample(progress: number, rest: Readonly<{ x: number; y: number; z: number }>): this {
    const s = sampleRepairSwing(progress, this.swing);
    const drop = SETUP_DROP * (1 - s.setup);
    this.plank.set(rest.x, rest.y - drop, rest.z - drop * 0.4);
    this.brace.set(this.plank.x + BRACE.x, this.plank.y + BRACE.y, this.plank.z + PLANK_FACE + BRACE.palmDepth);
    // Handle up the hammer's y; the face strikes along −x. Raising tips the head back over the wrist.
    const angle = s.raise * HAMMER_WINDUP_ANGLE, roll = HAMMER_ROLL;
    const y = this.axisY.set(-Math.sin(roll) * Math.cos(angle), Math.cos(roll) * Math.cos(angle), -Math.sin(angle));
    const x = this.axisX.set(Math.sin(roll) * Math.sin(angle), -Math.cos(roll) * Math.sin(angle), -Math.cos(angle));
    const z = Vector3.CrossToRef(x, y, this.axisZ);
    // At impact (handle tilted by the roll, face forward) the face centre lands on the nail;
    // the windup then lifts and draws the hand back from that grip while the head tips over it.
    this.grip.set(
      this.plank.x + PLANK_NAIL.x + HAMMER_HEAD * Math.sin(roll),
      this.plank.y + PLANK_NAIL.y - HAMMER_HEAD * Math.cos(roll) + s.raise * HAMMER_WINDUP_LIFT,
      this.plank.z + PLANK_NAIL.z - HAMMER_FACE - s.raise * HAMMER_WINDUP_DRAW);
    // The hand's axes, expressed in hammer space, carried into the action frame.
    const P = HAMMER_PALM, F = HAMMER_FINGERS;
    this.palm.set(x.x * P.x + y.x * P.y + z.x * P.z, x.y * P.x + y.y * P.y + z.y * P.z, x.z * P.x + y.z * P.y + z.z * P.z);
    this.fingers.set(x.x * F.x + y.x * F.y + z.x * F.z, x.y * F.x + y.y * F.y + z.y * F.z, x.z * F.x + y.z * F.y + z.z * F.z);
    Quaternion.FromRotationMatrixToRef(Matrix.FromXYZAxesToRef(x, y, z, this.basis), this.gripRotation).multiplyInPlace(HAMMER_TO_HAND);
    return this;
  }
}
